import { useCallback, useMemo } from 'react'

import {
  Custom3dOutline,
  Texture,
  Image2,
  Hexagon3d,
  CubeDraft,
  Animate,
  UserStar,
  Models,
  classnames,
} from '@cgtrader/triangle-ui/dist/src/ssr-index'

import useOpenedModelReadOnly from 'Pods/ThreeDStudio/Hooks/useOpenedModelReadOnly'
import { useThreeDStudioStore } from 'Pods/ThreeDStudio/Store'
import { setActiveViewerSidebarTab } from 'Pods/ThreeDStudio/Store/actions/ui'
import { VIEWER_SIDEBAR_TAB } from 'Pods/ThreeDStudio/Store/constants'

const NOT_OWNER_REASON = 'Variations are available only for your own models'

const useViewerSidebar = (isModelOpened = false) => {
  const isOpen = useThreeDStudioStore(state => state.ui?.isViewerSidebarOpen)
  const isUploadedModel = useThreeDStudioStore(state => !!state.modelSettings?.inputThreeDUrl)
  const isReadOnly = useOpenedModelReadOnly()
  const isEnhanceToolDisabled = !isModelOpened
  // Uploaded models have no generator session, so they can never have variations;
  // someone else's model exposes no variations the current user can manage.
  const isVariationsDisabled = isEnhanceToolDisabled || isUploadedModel || isReadOnly

  const handleRemeshClick = useCallback(() => {
    if (isEnhanceToolDisabled) {
      return
    }

    setActiveViewerSidebarTab(VIEWER_SIDEBAR_TAB.REMESH)
  }, [isEnhanceToolDisabled])

  const handleTextureClick = useCallback(() => {
    if (isEnhanceToolDisabled) {
      return
    }

    setActiveViewerSidebarTab(VIEWER_SIDEBAR_TAB.TEXTURE)
  }, [isEnhanceToolDisabled])

  const handleSegmentClick = useCallback(() => {
    if (isEnhanceToolDisabled) {
      return
    }

    setActiveViewerSidebarTab(VIEWER_SIDEBAR_TAB.SEGMENT)
  }, [isEnhanceToolDisabled])

  const handleAnimateClick = useCallback(() => {
    if (isEnhanceToolDisabled) {
      return
    }

    setActiveViewerSidebarTab(VIEWER_SIDEBAR_TAB.ANIMATE)
  }, [isEnhanceToolDisabled])

  const handleRenderClick = useCallback(() => {
    if (isEnhanceToolDisabled) {
      return
    }

    setActiveViewerSidebarTab(VIEWER_SIDEBAR_TAB.RENDER)
  }, [isEnhanceToolDisabled])

  const handleVariationsClick = useCallback(() => {
    if (isVariationsDisabled) {
      return
    }

    setActiveViewerSidebarTab(VIEWER_SIDEBAR_TAB.VARIATIONS)
  }, [isVariationsDisabled])

  const handleAssistClick = useCallback(() => {
    if (isEnhanceToolDisabled) {
      return
    }

    setActiveViewerSidebarTab(VIEWER_SIDEBAR_TAB.ASSIST)
  }, [isEnhanceToolDisabled])

  const navigationItems = useMemo(() => [
    {
      id: 'viewer-sidebar',
      label: 'Enhance 3D Model',
      icon: Custom3dOutline,
      className: classnames(
        'viewer-sidebar__item--first',
        isOpen && 'viewer-sidebar__item--first-opened',
      ),
      isStatic: true,
    },
    {
      id: VIEWER_SIDEBAR_TAB.REMESH,
      label: 'Remesh',
      icon: Hexagon3d,
      className: 'viewer-sidebar__item--second',
      onClick: handleRemeshClick,
      isDisabled: isEnhanceToolDisabled,
    },
    {
      id: VIEWER_SIDEBAR_TAB.TEXTURE,
      label: 'Texture',
      icon: Texture,
      onClick: handleTextureClick,
      isDisabled: isEnhanceToolDisabled,
    },
    {
      id: VIEWER_SIDEBAR_TAB.SEGMENT,
      label: 'Segment',
      icon: CubeDraft,
      onClick: handleSegmentClick,
      isDisabled: isEnhanceToolDisabled,
    },
    {
      id: VIEWER_SIDEBAR_TAB.ANIMATE,
      label: 'Animate',
      icon: Animate,
      onClick: handleAnimateClick,
      isDisabled: isEnhanceToolDisabled,
    },
    {
      id: VIEWER_SIDEBAR_TAB.RENDER,
      label: 'Render',
      icon: Image2,
      onClick: handleRenderClick,
      isDisabled: isEnhanceToolDisabled,
    },
    {
      id: VIEWER_SIDEBAR_TAB.VARIATIONS,
      label: 'Variations',
      icon: Models,
      onClick: handleVariationsClick,
      isDisabled: isVariationsDisabled,
      // An uploaded model is disabled for variations, but the user already has a
      // model open, so the "Generate or upload a model" tooltip would be misleading.
      // A non-owner gets an ownership-specific reason instead.
      hideDisabledTooltip: isUploadedModel && !isReadOnly,
      ...(isReadOnly && { disabledReason: NOT_OWNER_REASON }),
    },
    {
      id: VIEWER_SIDEBAR_TAB.ASSIST,
      label: 'Assist',
      icon: UserStar,
      onClick: handleAssistClick,
      isDisabled: isEnhanceToolDisabled,
    },
  ], [
    isOpen,
    isEnhanceToolDisabled,
    isVariationsDisabled,
    isUploadedModel,
    isReadOnly,
    handleRemeshClick,
    handleTextureClick,
    handleSegmentClick,
    handleAnimateClick,
    handleRenderClick,
    handleVariationsClick,
    handleAssistClick,
  ])

  return {
    isOpen,
    navigationItems,
  }
}

export default useViewerSidebar
